import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Grid, TextField } from "@mui/material";
import { blue } from "@mui/material/colors";
import { Visibility as VisibilityIcon, VisibilityOff as VisibilityOffIcon } from "@mui/icons-material";

import CardTable from "../../components/CardTable/CardTable";
import RegularButton from "../../components/CustomButtons/Button";

import { useForm } from "../../hooks/useForm";
import { useToggle } from "../../hooks/useToggle";

import { addNfcTag, editNfcTag } from "../../redux/actions/nfcTags/nfcTagsAction";

import { initialValues } from "./initialValues";
import { config } from "./config";
import { tagsColumnsAdded } from "./columns";

const colorValue = 300;

export const NfcTagForm = ({ onClose, isEdit = false, tag = {} }) => {
  const dispatch = useDispatch();
  const { isLoadingComp } = useSelector(({ search }) => search);
  const {
    inputValues,
    fieldErrors,
    handleInputOnChange,
    cleanForm,
    handleNewValues,
    runValidates,
  } = useForm(initialValues.inputValues, initialValues.required);

  const { uuidTag, brand, nip } = inputValues;
  const [show, setShow] = useToggle();
  const [tags, setTags] = useState([]);

  const addTag = () => {
    if (runValidates()) return;
    setTags((prev) => [...prev, { uuidTag, brand, nip }]);
    cleanForm();
  };

  const removeTag = (id) => {
    setTags(tags.filter((tg) => tg.uuidTag !== id));
  };

  const handleOnChangeNip = (e) => {
    if (/^\d*$/.test(e.target.value)) {
      handleInputOnChange({ ...e, value: e.target.value });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    let form = {};
    if (isEdit) {
      form = { uuidTag, brand, nip };
      dispatch(editNfcTag(tag.id, { ...form, status: tag.status }, onClose));
      return;
    } else {
      if (tags.length === 0) {
        if (runValidates()) return;
        const newTag = { uuidTag, brand, nip };
        form = { tags: [newTag] };
        dispatch(addNfcTag(form, onClose));
        return;
      }
      form = { tags: tags };
      dispatch(addNfcTag(form, onClose));
      return;
    }
  };

  useEffect(() => {
    isEdit ? handleNewValues({ ...tag, confirmNip: tag.nip }) : cleanForm();
  }, []);

  return (
    <form onSubmit={handleSubmit}>
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <TextField
            label={config.UUID.label}
            name="uuidTag"
            value={uuidTag}
            onChange={handleInputOnChange}
            variant="outlined"
            error={fieldErrors.uuidTag}
            helperText={fieldErrors.uuidTag || ""}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={5} md={isEdit ? 6 : 5}>
          <TextField
            label={config.BRAND.label}
            name="brand"
            value={brand}
            onChange={handleInputOnChange}
            variant="outlined"
            error={fieldErrors.brand}
            helperText={fieldErrors.brand || ""}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={5} md={isEdit ? 6 : 5}>
          <TextField
            type={show ? "text" : "password"}
            label={config.NIP.label}
            name="nip"
            value={nip}
            onChange={handleOnChangeNip}
            variant="outlined"
            fullWidth
            error={fieldErrors.nip}
            helperText={fieldErrors.nip || ""}
            InputProps={{
              inputMode: "numeric",
              endAdornment: (
                <RegularButton
                  justIcon
                  round
                  color="transparent"
                  onClick={setShow}
                  size="sm"
                >
                  {!show ? (
                    <VisibilityIcon style={{ color: blue[colorValue] }} />
                  ) : (
                    <VisibilityOffIcon style={{ color: blue[colorValue] }} />
                  )}
                </RegularButton>
              ),
            }}
          />
        </Grid>
        {!isEdit ? (
          <>
            <Grid
              item
              xs={12}
              sm={2}
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <RegularButton
                color="info"
                fullWidth
                onClick={addTag}
                justIcon
                round
                disabled={isEdit}
              >
                +
              </RegularButton>
            </Grid>
            {tags.length > 0 ? (
              <Grid item xs={12}>
                <CardTable
                  columns={tagsColumnsAdded(removeTag)}
                  data={tags}
                  showPagination={false}
                  pageSizeAuto
                />
              </Grid>
            ) : null}
          </>
        ) : null}
        <Grid item xs={12}>
          <RegularButton
            color="info"
            fullWidth
            onClick={handleSubmit}
            disabled={isLoadingComp?.loadingNfcTagCreate}
          >
            {isEdit ? "Editar NFC Tag" : "Crear NFC Tags"}
          </RegularButton>
        </Grid>
        <Grid item xs={12}>
          <RegularButton
            color="danger"
            fullWidth
            onClick={onClose}
            disabled={isLoadingComp?.loadingNfcTagCreate}
          >
            Cancelar
          </RegularButton>
        </Grid>
      </Grid>
    </form>
  );
};
